import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Star, Search, RefreshCcw, Trash2, Plus, Upload as UploadIcon, FileSpreadsheet } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import InfoModal from '../../components/InfoModal.jsx';
import readXlsxFile from 'read-excel-file/web-worker';

export default function AdminEvaluations({ partner, role }) {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'approved', 'pending'
  const [filterCourse, setFilterCourse] = useState('all'); // 'all' | course_id
  const [filterStars, setFilterStars] = useState('all'); // 'all' | '1'..'5' as minimum
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, evaluation: null });
  const [importModal, setImportModal] = useState(false);
  const [importCourseId, setImportCourseId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState({ items: [], total: 0, duplicates: 0, newCount: 0 });
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', content: '' });
  const [allCourses, setAllCourses] = useState([]);

  const ensureXLSX = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve();
    const s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.0/package/dist/xlsx.full.min.js';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });

  const downloadTemplate = async () => {
    try {
      await ensureXLSX();
      const headers = [['Nome','E-mail','Pontuação','Depoimento','Data']];
      const ws = window.XLSX.utils.aoa_to_sheet(headers);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'Avaliacoes');
      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modelo-avaliacoes.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Falha ao gerar modelo .xlsx', e);
      alert('Não foi possível gerar o modelo .xlsx. Tente novamente.');
    }
  };

  const exportFilteredToExcel = async () => {
    try {
      await ensureXLSX();
      const headers = ['Curso', 'Aluno', 'Empresa', 'Estrelas', 'Status', 'Comentário', 'Data'];
      const rows = filteredEvaluations.map((ev) => ([
        ev.course_name || '',
        ev.user_name || ev.author_name || ev.user_email || 'Usuário',
        ev.company_name || '',
        Number(ev.rating || 0),
        ev.approved ? 'Aprovado' : 'Oculto',
        ev.comment || '',
        ev.created_at ? new Date(ev.created_at).toLocaleDateString('pt-BR') : '',
      ]));

      const data = [headers, ...rows];
      const ws = window.XLSX.utils.aoa_to_sheet(data);

      ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
      ws['!autofilter'] = { ref: `A1:G${Math.max(1, data.length)}` };

      headers.forEach((_, idx) => {
        const cellRef = window.XLSX.utils.encode_cell({ r: 0, c: idx });
        if (!ws[cellRef]) return;
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          fill: { fgColor: { rgb: 'FF1E293B' } },
          alignment: { horizontal: 'center', vertical: 'center' }
        };
      });

      ws['!cols'] = headers.map((header, colIndex) => {
        const maxContentLength = data.reduce((maxLen, row) => {
          const cellValue = row[colIndex] == null ? '' : String(row[colIndex]);
          return Math.max(maxLen, cellValue.length);
        }, header.length);
        return { wch: Math.min(80, Math.max(12, maxContentLength + 2)) };
      });

      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'Avaliacoes');
      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avaliacoes-filtradas-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Falha ao exportar avaliações .xlsx', e);
      setInfoModal({ isOpen: true, title: 'Erro', content: 'Não foi possível exportar as avaliações para Excel.' });
    }
  };

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vw_course_evaluations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEvaluations(data || []);
    } catch (e) {
      console.error('Erro ao buscar avaliações:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name')
        .eq('status', 'Ativo')
        .order('name');
      if (error) throw error;
      setAllCourses(data || []);
    } catch (e) {
      console.error('Erro ao buscar cursos:', e);
    }
  };

  useEffect(() => {
    fetchEvaluations();
    fetchCourses();
  }, []);

  const parseFile = async () => {
    if (!importFile) return [];
    if (importFile.name.endsWith('.csv')) {
      const text = await importFile.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
      if (lines.length === 0) return [];
      const hdr = (lines.shift() || '').split(/[;,]/).map(h => h.trim().toLowerCase());
      const items = [];
      lines.forEach(line => {
        const sep = line.includes(';') ? ';' : ',';
        const parts = line.split(sep).map(c => c.trim());
        items.push({
          name: parts[hdr.indexOf('nome')] || '',
          email: parts[hdr.indexOf('e-mail')] || parts[hdr.indexOf('email')] || '',
          rating: parts[hdr.indexOf('pontuação')] || parts[hdr.indexOf('pontuacao')] || parts[2] || '',
          comment: parts[hdr.indexOf('depoimento')] || parts[3] || '',
          date: parts[hdr.indexOf('data')] || parts[4] || ''
        });
      });
      return items;
    } else {
      const sheet = await readXlsxFile(importFile);
      const dataRows = sheet.slice(1);
      return dataRows.map(r => ({
        name: r[0] ? String(r[0]).trim() : '',
        email: r[1] ? String(r[1]).trim() : '',
        rating: r[2] != null ? String(r[2]).trim() : '',
        comment: r[3] ? String(r[3]).trim() : '',
        date: r[4] ? String(r[4]).trim() : ''
      }));
    }
  };

  const normalize = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
  const makeKey = (r) => {
    const who = normalize(r.email) || normalize(r.name);
    return [String(r.rating || '').trim(), who, normalize(r.comment)].join('||');
  };

  const analyze = async () => {
    if (!importCourseId || !importFile) {
      setPreview({ items: [], total: 0, duplicates: 0, newCount: 0 });
      return;
    }
    setAnalyzing(true);
    try {
      const rows = await parseFile();
      const payload = rows
        .map(r => ({
          course_id: importCourseId,
          user_id: null,
          author_name: r.name || null,
          email: r.email || null,
          rating: Math.max(1, Math.min(5, parseInt(r.rating || '0', 10) || 0)) || 0,
          comment: r.comment || null,
          created_at: r.date && !isNaN(Date.parse(r.date)) ? new Date(r.date).toISOString() : new Date().toISOString(),
          submitted_at: r.date && !isNaN(Date.parse(r.date)) ? new Date(r.date).toISOString() : null,
          updated_at: new Date().toISOString(),
          approved: false
        }))
        .filter(p => p.rating > 0);

      if (payload.length === 0) {
        setPreview({ items: [], total: 0, duplicates: 0, newCount: 0 });
        return;
      }

      const { data: existing } = await supabase
        .from('course_evaluations')
        .select('email, author_name, rating, comment')
        .eq('course_id', importCourseId);

      const existingSet = new Set(
        (existing || []).map(e => makeKey({ rating: e.rating, email: e.email, name: e.author_name, comment: e.comment }))
      );
      const seenInFile = new Set();
      const items = payload.map(p => {
        const key = makeKey({ rating: p.rating, email: p.email, name: p.author_name, comment: p.comment });
        const dupDb = existingSet.has(key);
        const dupFile = seenInFile.has(key);
        if (!dupFile) seenInFile.add(key);
        return { payload: p, key, duplicate: dupDb || dupFile, reason: dupDb ? 'Já existe' : (dupFile ? 'Duplicada no arquivo' : '') };
      });

      const total = items.length;
      const duplicates = items.filter(i => i.duplicate).length;
      const newCount = total - duplicates;
      setPreview({ items, total, duplicates, newCount });
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    analyze();
  }, [importCourseId, importFile]);

  const handleToggleApproval = async (evaluation) => {
    if (role !== 'Admin') {
      setInfoModal({ isOpen: true, title: 'Permissão necessária', content: 'Apenas administradores podem moderar avaliações.' });
      return;
    }
    
    try {
      const newApprovedStatus = !evaluation.approved;
      const { error } = await supabase
        .from('course_evaluations')
        .update({ 
          approved: newApprovedStatus,
          approved_at: newApprovedStatus ? new Date().toISOString() : null,
          approved_by: newApprovedStatus ? (await supabase.auth.getUser()).data.user?.id : null
        })
        .eq('id', evaluation.id);

      if (error) throw error;

      setEvaluations(prev => prev.map(ev => 
        ev.id === evaluation.id 
          ? { ...ev, approved: newApprovedStatus } 
          : ev
      ));
    } catch (e) {
      console.error('Erro ao atualizar avaliação:', e);
      setInfoModal({ isOpen: true, title: 'Erro', content: 'Erro ao atualizar o status da avaliação.' });
    }
  };

  const filteredEvaluations = evaluations.filter(ev => {
    const matchesSearch = 
      ev.course_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ev.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ev.comment?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = 
      filterStatus === 'all' ? true :
      filterStatus === 'approved' ? ev.approved === true :
      filterStatus === 'pending' ? ev.approved !== true : true;
    
    const matchesCourse =
      filterCourse === 'all' ? true :
      (String(ev.course_id) === String(filterCourse));
    
    const matchesStars =
      filterStars === 'all' ? true : (Number(ev.rating || 0) >= Number(filterStars));
      
    return matchesSearch && matchesStatus && matchesCourse && matchesStars;
  });

  const askDelete = (evaluation) => {
    if (role !== 'Admin') return;
    setDeleteModal({ isOpen: true, evaluation });
  };

  const confirmDelete = async () => {
    const ev = deleteModal.evaluation;
    if (!ev) return;
    try {
      const { error } = await supabase
        .from('course_evaluations')
        .delete()
        .eq('id', ev.id);
      if (error) throw error;
      setEvaluations(prev => prev.filter(e => e.id !== ev.id));
    } catch (e) {
      console.error('Erro ao excluir avaliação:', e);
      setInfoModal({ isOpen: true, title: 'Erro', content: 'Não foi possível excluir a avaliação.' });
    } finally {
      setDeleteModal({ isOpen: false, evaluation: null });
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star 
            key={star} 
            size={14} 
            className={star <= rating ? "text-yellow-400 fill-yellow-400" : "text-slate-600"} 
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
        <div className="mb-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-white">Moderação de Avaliações</h3>
              <p className="text-slate-400 text-sm">Aprove ou oculte avaliações dos alunos sobre os cursos.</p>
            </div>
            <button
              onClick={fetchEvaluations}
              className="shrink-0 p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition"
              title="Atualizar"
            >
              <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full">
            <div className="relative w-full sm:flex-1 sm:min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Buscar avaliações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 transition"
              />
            </div>
          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 transition"
            title="Filtrar por curso"
          >
            <option value="all">Todos os Cursos</option>
            {allCourses.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.name}</option>
            ))}
          </select>
          <select
            value={filterStars}
            onChange={(e) => setFilterStars(e.target.value)}
            className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 transition"
            title="Filtrar por estrelas (mínimo)"
          >
            <option value="all">Todas as Estrelas</option>
            <option value="5">5 estrelas</option>
            <option value="4">4+ estrelas</option>
            <option value="3">3+ estrelas</option>
            <option value="2">2+ estrelas</option>
            <option value="1">1+ estrela</option>
          </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 transition"
            >
              <option value="all">Todos os Status</option>
              <option value="pending">Pendentes</option>
              <option value="approved">Aprovados</option>
            </select>
          {role === 'Admin' && (
            <button 
              onClick={exportFilteredToExcel}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold shadow"
              title="Exportar avaliações filtradas para Excel"
            >
              <FileSpreadsheet size={14} /> Exportar Excel
            </button>
          )}
          {role === 'Admin' && (
            <button 
              onClick={() => setImportModal(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold shadow"
              title="Importar avaliações via Excel"
            >
              <Plus size={14} /> Importar
            </button>
          )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredEvaluations.length === 0 ? (
          <div className="text-center py-12 bg-slate-950/50 rounded-xl border border-slate-800">
            <p className="text-slate-400">Nenhuma avaliação encontrada.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvaluations.map(ev => (
              <div key={ev.id} className="bg-slate-950 border border-slate-800 p-5 rounded-xl flex flex-col gap-3 relative overflow-hidden group">
                {/* Status Badge */}
                <div className={`absolute top-0 left-0 w-1 h-full ${ev.approved ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-white font-bold text-sm line-clamp-1" title={ev.course_name}>{ev.course_name}</h4>
                    <p className="text-slate-400 text-xs mt-1 flex items-center gap-2">
                      {ev.user_name || ev.author_name || ev.user_email || 'Usuário'}
                      {ev.company_name && <span className="px-1.5 py-0.5 bg-slate-800 rounded text-[10px]">{ev.company_name}</span>}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {renderStars(ev.rating)}
                    <span className="text-[10px] text-slate-500">
                      {new Date(ev.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>

                <div className="bg-slate-900 rounded-lg p-3 text-sm text-slate-300 min-h-[60px] italic border border-slate-800/50 relative">
                  &quot;{ev.comment || 'Sem comentário escrito.'}&quot;
                </div>

                <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-800/50">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1 ${ev.approved ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {ev.approved ? <><Check size={12} /> Aprovado</> : <><X size={12} /> Oculto</>}
                    </span>
                  </div>
                  
                  {role === 'Admin' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => askDelete(ev)}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition shadow-lg bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1"
                        title="Excluir avaliação"
                      >
                        <Trash2 size={14} /> Excluir
                      </button>
                      <button
                        onClick={() => handleToggleApproval(ev)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition shadow-lg ${
                          ev.approved 
                            ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700' 
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        {ev.approved ? 'Ocultar' : 'Aprovar'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal 
        isOpen={deleteModal.isOpen}
        title="Excluir avaliação"
        message="Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ isOpen: false, evaluation: null })}
      />

      {importModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Importar Avaliações</h3>
              <button onClick={() => setImportModal(false)} className="text-slate-400 hover:text-white"><X size={18}/></button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Curso</label>
                <select
                  value={importCourseId}
                  onChange={(e) => setImportCourseId(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                >
                  <option value="">Selecione o curso...</option>
                  {allCourses.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Arquivo (.xlsx ou .csv)</label>
                <label className="flex items-center gap-2 justify-center px-4 py-8 border border-slate-700 rounded-lg bg-slate-950 cursor-pointer hover:border-slate-600">
                  <UploadIcon size={20} className="text-slate-400" />
                  <span className="text-slate-300 text-sm">{importFile ? importFile.name : 'Selecionar arquivo'}</span>
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e)=> setImportFile(e.target.files[0] || null)} />
                </label>
                <div>
                  <button onClick={downloadTemplate} className="mt-1 text-xs font-bold px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white">Baixar modelo (.xlsx)</button>
                </div>
              </div>

              <div className="mt-2 bg-slate-950 border border-slate-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-400">Pré-visualização</span>
                  <span className="text-xs text-slate-500">{analyzing ? 'Analisando...' : `${preview.total} mapeadas • ${preview.newCount} novas • ${preview.duplicates} duplicadas`}</span>
                </div>
                <div className="max-h-48 overflow-auto space-y-2 pr-1">
                  {preview.items.map((it, idx) => (
                    <div key={idx} className={`text-xs p-2 rounded border ${it.duplicate ? 'border-rose-700 bg-rose-950/30 text-rose-300' : 'border-slate-800 bg-slate-900 text-slate-300'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{it.payload.author_name || it.payload.email || 'Sem nome'}</span>
                        <span className="text-[10px]">{it.payload.rating}★</span>
                      </div>
                      <div className="line-clamp-1">{it.payload.comment || 'Sem comentário'}</div>
                      {it.duplicate && <div className="text-[10px] font-bold">Duplicada</div>}
                    </div>
                  ))}
                  {preview.items.length === 0 && (
                    <div className="text-xs text-slate-500">Selecione o curso e o arquivo para visualizar as avaliações detectadas.</div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-2">
                <button onClick={()=>setImportModal(false)} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
                <button onClick={async ()=>{
                  if (!importCourseId || !importFile) { setInfoModal({ isOpen: true, title: 'Campos obrigatórios', content: 'Selecione o curso e o arquivo para importar.' }); return; }
                  if (preview.items.length === 0) { await analyze(); }
                  setImporting(true);
                  try {
                    const toInsert = preview.items.filter(it => !it.duplicate).map(it => it.payload);
                    if (toInsert.length === 0) { setInfoModal({ isOpen: true, title: 'Sem novidades', content: 'Nenhuma avaliação nova a importar. Todas as linhas do arquivo são duplicadas para este curso.' }); return; }
                    const { error } = await supabase.from('course_evaluations').insert(toInsert);
                    if (error) {
                      const msg = error?.message || 'Erro desconhecido';
                      throw new Error(msg);
                    }
                    setImportModal(false);
                    setImportFile(null);
                    setImportCourseId('');
                    await fetchEvaluations();
                    setInfoModal({ isOpen: true, title: 'Importação concluída', content: `${toInsert.length} avaliações importadas. ${preview.duplicates} duplicadas foram ignoradas.` });
                  } catch (e) {
                    console.error(e);
                    setInfoModal({ isOpen: true, title: 'Falha na importação', content: String(e?.message || e) });
                  } finally { setImporting(false); }
                }}
                className={`px-6 py-2 rounded-lg text-white font-bold shadow-lg ${importing? 'bg-slate-700':'bg-orange-600 hover:bg-orange-500'}`}
                disabled={importing}
                >{importing? 'Importando...':'Importar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InfoModal 
        isOpen={infoModal.isOpen}
        title={infoModal.title}
        content={infoModal.content}
        onClose={() => setInfoModal({ ...infoModal, isOpen: false })}
      />
    </div>
  );
}
